/**
 * Environment-based feature flags.
 *
 * A flag is ON when either:
 *   - the env-scoped var `VITE_FEATURE_<NAME>=true` is set, OR
 *   - the aggregate `VITE_FEATURE_FLAGS` (comma list of flag names)
 *     contains the flag.
 *
 * This lets staging enable in-progress features independently of
 * production without any code branches per environment.
 *
 * Usage:
 *   import { isFeatureEnabled } from "@/lib/featureFlags";
 *   if (isFeatureEnabled("new_organizer_analytics")) { ... }
 *
 * See docs/deployment/SECRETS.md.
 */

const truthy = new Set(["1", "true", "yes", "on"]);

function readAggregate(): Set<string> {
  const raw = (import.meta.env.VITE_FEATURE_FLAGS as string | undefined) ?? "";
  return new Set(
    raw
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
  );
}

const aggregate = readAggregate();

export function isFeatureEnabled(name: string): boolean {
  const normalized = name.trim().toLowerCase();
  if (!normalized) return false;
  if (aggregate.has(normalized)) return true;
  const key = `VITE_FEATURE_${normalized.toUpperCase()}`;
  const raw = (import.meta.env as Record<string, string | undefined>)[key];
  return !!raw && truthy.has(raw.trim().toLowerCase());
}

export function activeFeatureFlags(): string[] {
  return Array.from(aggregate).sort();
}
